// src/pages/PrivacyPolicy.tsx
import React, { useEffect, useState } from 'react';
import Footer from '../../components/Footer';
import {
  BulletItem,
  BulletList,
  Emphasis,
  InnerCard,
  InnerContainer,
  InnerSection,
  PageTitle,
  Paragraph,
  SectionTitle,
  TextLink,
} from '../../components/InnerPageLayout';
import JsonData from '../../data/data.json';
import { SiteData } from '../../types/siteDataTypes';
import InnerPageHeader from '../../components/InnerPageHeader';

const PrivacyPolicy: React.FC = () => {
  const [siteData, setSiteData] = useState<SiteData | null>(null);

  useEffect(() => {
    setSiteData(JsonData as SiteData);
  }, []);
  
  const APP_NAME = 'Chot Study';

  return (
    <>
      {siteData ? (
        <>
          <InnerPageHeader data={siteData.ChotStudyPrivacyHeader} />
          <InnerSection>
            <InnerContainer>
              <InnerCard>
                <PageTitle>Privacy Policy</PageTitle>
                <Paragraph>Effective Date: {siteData.AppsData.effectiveDate}</Paragraph>

                <SectionTitle>1. Introduction</SectionTitle>
                <Paragraph>
                  Chots AI ("we," "our," or "us") owns and operates the {APP_NAME} app ("the App").
                  We value your privacy and are committed to protecting your personal information.
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your
                  information when you use our App. By using the App, you consent to the practices
                  described in this Privacy Policy.
                </Paragraph>

                <SectionTitle>2. Information We Collect</SectionTitle>
                <Paragraph>
                  <Emphasis>Personal Information:</Emphasis> When you register to use the App, we may
                  collect personal information such as your name and email address.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Personal Context ("About You"):</Emphasis> Information about yourself
                  that you provide through the App's "About You" section, including personal
                  preferences, goals, interests, and other relevant data. This information is
                  end-to-end encrypted and only accessible to you and the AI assistants.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Chat Messages:</Emphasis> All conversations between you and the AI
                  assistants are end-to-end encrypted and stored securely. Only you and the AI
                  assistants can access the content of your messages. This data is retained to
                  enhance the quality and relevance of responses provided by the AI assistants.
                </Paragraph>

                <SectionTitle>3. How We Use Your Information</SectionTitle>
                <Paragraph>We use the information we collect to:</Paragraph>
                <BulletList>
                  <BulletItem>Provide, operate, and maintain the App.</BulletItem>
                  <BulletItem>
                    Enhance and personalize your experience with the App by tailoring responses
                    based on your personal context.
                  </BulletItem>
                  <BulletItem>Conduct internal analytics to improve our services.</BulletItem>
                  <BulletItem>Comply with legal obligations.</BulletItem>
                </BulletList>

                <SectionTitle>4. Data Retention</SectionTitle>
                <Paragraph>
                  <Emphasis>Chat Messages:</Emphasis> Your encrypted chat messages are stored for a
                  period of up to 1 year from the date of creation. After this period, the messages
                  may be automatically deleted from our systems. This retention period is
                  implemented to ensure that the AI assistants can provide accurate and
                  contextually relevant responses over time.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Personal Context:</Emphasis> You can update, change, or delete your
                  personal context details at any time through the App's settings. Deleted
                  information will be removed from our active databases but may remain in backup
                  archives for a limited time.
                </Paragraph>

                <SectionTitle>5. Data Sharing and Disclosure</SectionTitle>
                <Paragraph>
                  We do not sell, trade, or rent your personal information to third parties.
                  However, we may share your data with:
                </Paragraph>
                <Paragraph>
                  <Emphasis>Internal Analytics:</Emphasis> Your data may be shared within Chots AI
                  for internal analytics to enhance our services.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Third-Party Services:</Emphasis> We integrate with third-party services,
                  such as RevenueCat for subscription management and Datadog for logging. These
                  services may have access to your information as necessary for them to perform
                  their functions, but they are obligated not to disclose or use it for any other
                  purpose.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Legal Requirements:</Emphasis> We may disclose your information if
                  required to do so by law or in response to valid requests by public authorities.
                </Paragraph>

                <SectionTitle>6. Your Rights and Choices</SectionTitle>
                <Paragraph>
                  <Emphasis>Access and Correction:</Emphasis> You have the right to access and
                  correct your personal information at any time. You can do this by logging into
                  your account and accessing the account settings.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Data Deletion:</Emphasis> You may request the deletion of your personal
                  data by contacting us directly. Please note that certain data may be retained as
                  required by law or for legitimate business purposes.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Opt-Out:</Emphasis> You may opt out of certain data collection practices
                  by adjusting your preferences in the App's settings. However, opting out may
                  affect the functionality of the App.
                </Paragraph>

                <SectionTitle>7. Security of Your Information</SectionTitle>
                <Paragraph>
                  We implement comprehensive security measures to protect your personal
                  information:
                </Paragraph>
                <BulletList>
                  <BulletItem>
                    <Emphasis>End-to-End Encryption:</Emphasis> Your personal context and messages
                    are encrypted using AES-CBC encryption with 256-bit keys.
                  </BulletItem>
                  <BulletItem>
                    <Emphasis>Secure Transmission:</Emphasis> All data is transmitted over secure,
                    encrypted connections.
                  </BulletItem>
                  <BulletItem>
                    <Emphasis>Access Controls:</Emphasis> Only you and the AI assistants can access
                    your encrypted data.
                  </BulletItem>
                  <BulletItem>
                    <Emphasis>Regular Security Updates:</Emphasis> We continuously update our
                    security measures to protect against new threats.
                  </BulletItem>
                </BulletList>
                <Paragraph>
                  However, please be aware that no method of transmitting information over the
                  Internet or storing data is completely secure, and we cannot guarantee absolute
                  security.
                </Paragraph>

                <SectionTitle>8. Health & Wellness Disclaimer</SectionTitle>
                <Paragraph>
                  <Emphasis>Not Medical Advice:</Emphasis> The AI assistants in {APP_NAME}, including
                  but not limited to the Wellness Guide, Health & Symptom Guide, and Psychology
                  Companion, are for informational purposes only. They do not provide medical,
                  psychological, or professional healthcare advice.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Consult Professionals:</Emphasis> Always seek the advice of qualified
                  healthcare providers for medical concerns, mental health issues, or before making
                  any decisions related to your health.
                </Paragraph>
                <Paragraph>
                  <Emphasis>Emergency Situations:</Emphasis> Do not use this App for emergency
                  situations. If you are experiencing a medical or mental health emergency, contact
                  emergency services immediately.
                </Paragraph>

                <SectionTitle>9. Data Protection Rights (GDPR)</SectionTitle>
                <Paragraph>
                  If you are a resident of the European Economic Area (EEA), you have certain data
                  protection rights under the General Data Protection Regulation (GDPR):
                </Paragraph>
                <BulletList>
                  <BulletItem>
                    <Emphasis>Right to Access:</Emphasis> Request copies of your personal data.
                  </BulletItem>
                  <BulletItem>
                    <Emphasis>Right to Rectification:</Emphasis> Request correction of inaccurate
                    personal data.
                  </BulletItem>
                  <BulletItem>
                    <Emphasis>Right to Erasure:</Emphasis> Request deletion of your personal data.
                  </BulletItem>
                  <BulletItem>
                    <Emphasis>Right to Data Portability:</Emphasis> Request transfer of your data to
                    another service.
                  </BulletItem>
                  <BulletItem>
                    <Emphasis>Right to Restrict Processing:</Emphasis> Request limitation of
                    processing your personal data.
                  </BulletItem>
                  <BulletItem>
                    <Emphasis>Right to Object:</Emphasis> Object to processing of your personal
                    data.
                  </BulletItem>
                </BulletList>
                <Paragraph>
                  To exercise these rights, please contact us using the information provided
                  below.
                </Paragraph>

                <SectionTitle>10. Children's Privacy</SectionTitle>
                <Paragraph>
                  The App is not intended for use by individuals under the age of 16. We do not
                  knowingly collect personal information from children under 16. If we become aware
                  that we have inadvertently collected such information, we will take steps to
                  delete it as soon as possible.
                </Paragraph>

                <SectionTitle>11. Changes to This Privacy Policy</SectionTitle>
                <Paragraph>
                  We may update this Privacy Policy from time to time to reflect changes in our
                  practices or for other operational, legal, or regulatory reasons. When we make
                  changes, we will update the "Effective Date" at the top of this document. Your
                  continued use of the App after any such changes constitutes your acceptance of
                  the revised Privacy Policy.
                </Paragraph>

                <SectionTitle>12. Contact Us</SectionTitle>
                <Paragraph>
                  If you have any questions or concerns about this Privacy Policy, or if you would
                  like to make any privacy-related requests, please contact us at{' '}
                  <TextLink
                    href={`mailto:${siteData.AppsData.customerSupportEmail}?subject=Regarding ${APP_NAME}'s Privacy Policy`}
                  >
                    {siteData.AppsData.customerSupportEmail}
                  </TextLink>
                  .
                </Paragraph>
              </InnerCard>
            </InnerContainer>
          </InnerSection>
          <Footer />
        </>
      ) : (
        <p>Loading...</p>
      )}
    </>
  );
};

export default PrivacyPolicy;
